import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authApi } from '../api/services';
import BrandMark from '../components/BrandMark';
import AuthBackdrop from '../components/auth/AuthBackdrop';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

// ---------------------------------------------------------------------------
// ForgotPasswordPage — unauthenticated route. Auth-flow surfaces share a
// signature dark-glass card centered over a gradient mesh; the same backdrop
// is used by Signup / PasswordReset / Mfa setup so the auth funnel reads as
// one experience.
// ---------------------------------------------------------------------------

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0A0E17]">
      <AuthBackdrop />

      <div
        className="relative z-10 mx-4 w-full max-w-[420px] rounded-2xl border border-white/10 p-10 shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_80px_rgba(124,58,237,0.05)]"
        style={{
          background: 'rgba(18, 24, 41, 0.85)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <div className="mb-1">
          <BrandMark size="md" showStratiumLine accent="light" />
        </div>
        <p className="mb-8 text-[13px] tracking-wide text-white/60">Forgot Password</p>

        {error && (
          <Alert variant="error" className="mb-3">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {submitted ? (
          <Alert variant="success" className="mb-6">
            <AlertDescription>
              If an account exists for <strong>{email}</strong>, we've sent a password reset link. Check your inbox.
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <p className="text-[13px] text-white/60">
              Enter your email and we'll send you a link to reset your password.
            </p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-white/[0.03] text-white"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              size="lg"
              className="h-12 gap-2 bg-gradient-to-br from-violet-600 to-violet-700 text-base font-semibold text-white shadow-[0_4px_14px_rgba(124,58,237,0.3)] hover:from-violet-700 hover:to-violet-800"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Send Reset Link'}
            </Button>
          </form>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="text-xs font-semibold text-violet-400 hover:underline"
          >
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
